import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { addMonths, beginningOfToday } from 'lib/datetime/dateUtils'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import AccommodationCalendarDayPicker from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarDayPicker'
import type {
  AccommodationDayPickerSize,
  AccommodationMobileDayCellSelectHandler,
} from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendar.types'
import type { AccommodationDayCellVariant } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarLargeDayCell'
import { fillArray } from 'lib/array/arrayUtils'
import type { AccommodationCalendarYearSelectHandler, AccommodationMonthView } from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarMonthPicker'
import AccommodationCalendarMonthPicker from 'components/AccommodationKit/AccommodationCalendar/AccommodationCalendarMonthPicker'
import noop from 'lib/function/noop'
import { staticDayViews } from 'lib/datetime/calendarDayViews'

const now = beginningOfToday()

/**
 * This date picker isn't run from service calendar data so we don't have a time period of which dates to show
 * Lets a fairly large selection of time - 3 years (36 months) worth of days/month to pick from
 */
const monthViews: Array<AccommodationMonthView> = fillArray(36).map((index): AccommodationMonthView => {
  const month = addMonths(now, index)
  return {
    month: month.getMonth(),
    year: month.getFullYear(),
    durationUnit: 'night',
    soldOut: false,
  }
})

interface Props {
  checkInDate?: string
  checkOutDate?: string
  /**
   * Reference to the scroll container element this picker sits in
   * This is necessary to provide so it can manage showing the correct month
   */
  scrollerRef: React.RefObject<HTMLElement | null>
  size: AccommodationDayPickerSize
  /**
   * The month we're picking the date for inso ISO date format
   */
  selectedMonth?: string
  /**
   * Event for when the date picker wants to go back to the month picker
   */
  onBackToMonth?: () => void
  /**
   * What happens when the user selects a month from the mont view
   */
  onMonthSelect?: AccommodationCalendarYearSelectHandler

  variant?: 'both' | 'day-only'

  /**
   * Event for when the selected dates change
   */
  onDatesChange?: AccommodationMobileDayCellSelectHandler

  availabilityVariantMap?: Map<string, AccommodationDayCellVariant>
  renderFromStart?: boolean
}

function OfferDatePicker({
  checkInDate,
  scrollerRef,
  checkOutDate,
  size,
  selectedMonth,
  onMonthSelect,
  onBackToMonth,
  variant = 'both',
  onDatesChange = noop,
  availabilityVariantMap,
  renderFromStart,
}: Props) {
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const onDatesSelect = useCallback<AccommodationMobileDayCellSelectHandler>(day => {
    onDatesChange(day)
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.DateChanged)
  }, [onDatesChange, onUserAnalyticsAction])

  const month = useMemo(() => selectedMonth ? new Date(selectedMonth) : undefined, [selectedMonth])

  useEffect(() => {
    if (!month) {
      scrollerRef.current?.scrollTo({ behavior: 'auto', top: 0 })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [month])

  if (variant === 'day-only') {
    return <AccommodationCalendarDayPicker
      key={month ? `${month.getFullYear()}-${month.getMonth()}` : 'default'}
      startDate={checkInDate}
      endDate={checkOutDate}
      onDaySelect={onDatesSelect}
      contentRef={scrollerRef}
      views={staticDayViews}
      onBackToMonth={onBackToMonth}
      initialMonth={month?.getMonth()}
      initialYear={month?.getFullYear()}
      size={size}
      availabilityVariantMap={availabilityVariantMap}
      renderFromStart={renderFromStart}
    />
  }

  return <>
    {month && <AccommodationCalendarDayPicker
      startDate={checkInDate}
      endDate={checkOutDate}
      onDaySelect={onDatesChange}
      contentRef={scrollerRef}
      views={staticDayViews}
      onBackToMonth={onBackToMonth}
      initialMonth={month.getMonth()}
      initialYear={month.getFullYear()}
      size={size}
      availabilityVariantMap={availabilityVariantMap}
      renderFromStart={renderFromStart}
    />}
    {!month && <AccommodationCalendarMonthPicker
      views={monthViews}
      onSelect={onMonthSelect}
    />}
  </>
}

export default withOfferPageState(state => ({
  open: !!state.activeFilter || state.activeHeaderFilter === 'checkin',
}), OfferDatePicker, preventRenderWithClosedFilter)
